import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocDeleteEvent } from '@common/collab/events/collab-doc-delete.event';
import { CollabDocMergeEvent } from '@common/collab/events/collab-doc-merge.event';
import { CollabDocRenameEvent } from '@common/collab/events/collab-doc-rename.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { basename } from '@common/util/path';
import { CollabDocRevisionDateFormat } from '@portal-core/collab/constants/collab-doc-revision-date-format.constant';
import { CurrentService } from '@portal-core/current/services/current.service';
import { StringService } from '@portal-core/general/services/string.service';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { SnackBarWarningComponent } from '@portal-core/ui/snack-bar/components/snack-bar-warning/snack-bar-warning.component';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { first, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CollabEventSnackBarService {
  constructor(
    private currentService: CurrentService,
    private licenseUsersService: LicenseUsersService,
    private snackBar: MatSnackBar,
    private stringService: StringService
  ) { }

  public onCollabDocCommitted(event: CollabDocCommitEvent, activeDocId: string) {
    // If the committed doc is open in the editor then show a message in the snack bar
    if (event.docIds.includes(activeDocId)) {
      let message$: Observable<string>;
      let showWarningSnackBar = false;

      // If the another user committed the file then show the message with their name
      if (event.userId && event.userId !== this.currentService.getUserId()) {
        showWarningSnackBar = true;
        message$ = this.licenseUsersService.getLicenseUserByUserId$(event.userId, this.currentService.getActiveLicenseId()).pipe(
          first(licenseUser => !!licenseUser),
          map(licenseUser => `Changes have been committed by ${licenseUser.User.FullName}.`)
        );
      }
      // Else show a message without the user name
      else {
        message$ = of('Changes have been committed.');
      }

      // Display the message in the snack bar
      message$.subscribe(message => {
        this.showSnackBar(message, showWarningSnackBar);
      });
    }
  }

  public onCollabDocDeleted(event: CollabDocDeleteEvent, activeDocId: string) {
    // If the deleted doc is open in the editor then navigate away and show a message in the snack bar
    if (activeDocId === event.docId) {
      let message$: Observable<string>;
      let showWarningSnackBar = false;

      // If the another user deleted the file then show the message with their name
      if (event.source === 'user' && event.userId && event.userId !== this.currentService.getUserId()) {
        showWarningSnackBar = true;
        message$ = this.licenseUsersService.getLicenseUserByUserId$(event.userId, this.currentService.getActiveLicenseId()).pipe(
          first(licenseUser => !!licenseUser),
          map(licenseUser => `File has been deleted by ${licenseUser.User.FullName}.`)
        );
      } else if (event.source === 'system') {
        showWarningSnackBar = true;
        message$ = of(`File has been deleted in an external commit.`);
      } else {
        message$ = of('File has been deleted.');
      }

      // Display the message in the snack bar
      message$.subscribe(message => {
        this.showSnackBar(message, showWarningSnackBar);
      });
    }
  }

  public onCollabDocMerged(event: CollabDocMergeEvent, activeDocId: string) {
    // If the merged doc is open in the editor then show a message in the snack bar
    if (activeDocId === event.docId) {
      let message$: Observable<string>;
      let showWarningSnackBar = false;

      // If the another user merged the file then show the message with their name
      if (event.source === 'user' && event.userId && event.userId !== this.currentService.getUserId()) {
        showWarningSnackBar = true;
        message$ = this.licenseUsersService.getLicenseUserByUserId$(event.userId, this.currentService.getActiveLicenseId()).pipe(
          first(licenseUser => !!licenseUser),
          map(licenseUser => `The latest changes have been merged into "${basename(event.filePath)}" by ${licenseUser.User.FullName}.`)
        );
      }
      // Else show a message without the user name
      else {
        message$ = of(`The latest changes have been merged into "${basename(event.filePath)}".`);
      }

      // Display the message in the snack bar
      message$.subscribe(message => {
        this.showSnackBar(message, showWarningSnackBar);
      });
    }
  }

  public onCollabDocRenamed(event: CollabDocRenameEvent, activeDocId: string) {
    // If the renamed doc is open in the editor then navigate to the new path and show a message in the snack bar
    if (activeDocId === event.oldDocId) {
      let message$: Observable<string>;
      let showWarningSnackBar = false;

      // If the another user renamed the file then show the message with their name
      if (event.userId && event.userId !== this.currentService.getUserId()) {
        showWarningSnackBar = true;
        message$ = this.licenseUsersService.getLicenseUserByUserId$(event.userId, this.currentService.getActiveLicenseId()).pipe(
          first(licenseUser => !!licenseUser),
          map(licenseUser => `File has been renamed by ${licenseUser.User.FullName}.`)
        );
      }
      // Else show a message without the user name
      else {
        message$ = of('File has been renamed.');
      }

      // Display the message in the snack bar
      message$.subscribe(message => {
        this.showSnackBar(message, showWarningSnackBar);
      });
    }
  }

  public onCollabDocRestored(event: CollabDocRestoreEvent, activeDocId: string) {
    // If the restored doc is open in the editor then navigate to the new path and show a message in the snack bar
    if (activeDocId === event.docId) {
      let message$: Observable<string>;
      let showWarningSnackBar = false;

      // If the another user restored the file then show the message with their name
      if (event.userId && event.userId !== this.currentService.getUserId()) {
        showWarningSnackBar = true;
        message$ = this.licenseUsersService.getLicenseUserByUserId$(event.userId, this.currentService.getActiveLicenseId()).pipe(
          first(licenseUser => !!licenseUser),
          map(licenseUser => `File has been reverted to the version from ${this.stringService.formatDate(event.restoredFrom, CollabDocRevisionDateFormat)} by ${licenseUser.User.FullName}.`)
        );
      }
      // Else show a message without the user name
      else {
        message$ = of(`File has been reverted to the version from ${this.stringService.formatDate(event.restoredFrom, CollabDocRevisionDateFormat)}`);
      }

      // Display the message in the snack bar
      message$.subscribe(message => {
        this.showSnackBar(message, showWarningSnackBar);
      });
    }
  }

  private showSnackBar(message: string, showWarningSnackBar: boolean) {
    if (showWarningSnackBar) {
      this.snackBar.openFromComponent(SnackBarWarningComponent, {
        data: message
      });
    } else {
      this.snackBar.open(message, null, {
        duration: SnackBarAutoDismissDurationMS,
        panelClass: 'mc-snack-bar-panel-shrink'
      });
    }
  }
}
